/**
 * Verification Code Input Component
 * 6-digit code input for fallback authentication when PKCE fails
 */

import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mail, AlertCircle, CheckCircle, Loader2, ArrowRight } from 'lucide-react';

interface VerificationCodeInputProps {
  email: string;
  purpose: 'password_reset' | 'magic_link';
  onSuccess: (sessionUrl: string) => void;
  onCancel: () => void;
}

export const VerificationCodeInput: React.FC<VerificationCodeInputProps> = ({
  email,
  purpose,
  onSuccess,
  onCancel
}) => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [codeSent, setCodeSent] = useState(false);
  const [countdown, setCountdown] = useState(600); // 10 minutes in seconds
  const [resendCooldown, setResendCooldown] = useState(0);

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Countdown timer for code expiration
  useEffect(() => {
    if (!codeSent) return;

    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 0) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [codeSent]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown === 0) return;

    const timer = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 0) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [resendCooldown]);

  // Send verification code
  const sendVerificationCode = async () => {
    // Validate email before sending
    if (!email || !email.includes('@')) {
      setError('Invalid email address. Please go back and enter your email.');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/auth/send-verification-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, purpose }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send verification code');
      }

      console.log('[VerificationCode] Code sent:', data);

      // In development, show the code in console
      if (data.dev_only && data.code) {
        console.warn('🔐 [DEV ONLY] Verification code:', data.code);
      }

      setCodeSent(true);
      setCountdown(data.expiresIn || 600);
      setResendCooldown(60); // 60 second cooldown for resend

      // Focus first input
      inputRefs.current[0]?.focus();
    } catch (err: any) {
      console.error('[VerificationCode] Send error:', err);
      setError(err.message || 'Failed to send verification code');
    } finally {
      setIsLoading(false);
    }
  };

  // Send code on mount
  useEffect(() => {
    sendVerificationCode();
  }, []);

  // Handle input change
  const handleInputChange = (index: number, value: string) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError(null);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits entered
    if (newCode.every(digit => digit !== '') && newCode.join('').length === 6) {
      verifyCode(newCode.join(''));
    }
  };

  // Handle backspace
  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  // Handle paste
  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();

    // Only accept 6 digits
    if (!/^\d{6}$/.test(pastedData)) {
      setError('Please paste a valid 6-digit code');
      return;
    }

    const newCode = pastedData.split('');
    setCode(newCode);
    setError(null);

    // Focus last input
    inputRefs.current[5]?.focus();

    // Auto-verify
    verifyCode(pastedData);
  };

  // Verify code
  const verifyCode = async (codeString: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/auth/verify-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code: codeString }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Invalid verification code');
      }

      console.log('[VerificationCode] Verification successful:', data);

      // Success! Redirect to session URL
      onSuccess(data.sessionUrl);
    } catch (err: any) {
      console.error('[VerificationCode] Verify error:', err);
      setError(err.message || 'Invalid or expired code');
      setCode(['', '', '', '', '', '']); // Clear code
      inputRefs.current[0]?.focus(); // Focus first input
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!codeSent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-black flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 shadow-2xl border border-purple-500/20 max-w-md w-full text-center"
        >
          <Loader2 className="w-12 h-12 text-purple-400 animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Sending verification code...</h2>
          <p className="text-purple-300 text-sm">Please wait a moment</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-black flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 shadow-2xl border border-purple-500/20 max-w-md w-full"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Mail className="w-8 h-8 text-purple-400" />
          </div>
          <h2 className="text-2xl font-semibold text-white mb-2">
            Enter Verification Code
          </h2>
          <p className="text-purple-300 text-sm">
            We sent a 6-digit code to
          </p>
          <p className="text-white font-medium mt-1">{email}</p>
        </div>

        {/* Code Inputs */}
        <div className="flex gap-2 justify-center mb-6" onPaste={handlePaste}>
          {code.map((digit, index) => (
            <input
              key={index}
              ref={el => inputRefs.current[index] = el}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={e => handleInputChange(index, e.target.value)}
              onKeyDown={e => handleKeyDown(index, e)}
              disabled={isLoading}
              className="w-12 h-14 text-center text-2xl font-bold bg-white/5 border-2 border-purple-500/30 rounded-lg text-white focus:outline-none focus:border-purple-400 focus:bg-white/10 transition disabled:opacity-50"
              autoComplete="off"
            />
          ))}
        </div>

        {/* Error Message */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-200 text-sm flex items-start gap-2"
          >
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </motion.div>
        )}

        {/* Countdown Timer */}
        <div className="text-center mb-6">
          <p className="text-purple-300 text-sm">
            Code expires in:{' '}
            <span className={`font-mono font-semibold ${countdown < 60 ? 'text-red-400' : 'text-white'}`}>
              {formatTime(countdown)}
            </span>
          </p>
        </div>

        {/* Resend Button */}
        <div className="text-center mb-6">
          <button
            type="button"
            onClick={sendVerificationCode}
            disabled={isLoading || resendCooldown > 0}
            className="text-purple-300 hover:text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {resendCooldown > 0
              ? `Resend code in ${resendCooldown}s`
              : 'Didn\'t receive the code? Resend'}
          </button>
        </div>

        {/* Info */}
        <div className="bg-blue-900/30 border border-blue-500/30 rounded-lg p-4 mb-6">
          <p className="text-blue-200 text-xs leading-relaxed">
            <strong>Note:</strong> Enter the 6-digit code from your email. The code will expire
            in 10 minutes for security. Check your spam folder if you don't see it.
          </p>
        </div>

        {/* Cancel Button */}
        <button
          type="button"
          onClick={onCancel}
          disabled={isLoading}
          className="w-full py-3 bg-white/5 border border-purple-500/30 text-purple-300 rounded-lg hover:bg-white/10 transition disabled:opacity-50"
        >
          Cancel
        </button>

        {/* Loading Overlay */}
        {isLoading && (
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm rounded-2xl flex items-center justify-center">
            <div className="text-center">
              <Loader2 className="w-8 h-8 text-purple-400 animate-spin mx-auto mb-2" />
              <p className="text-white text-sm">Verifying...</p>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};
